import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GavelIcon from "@mui/icons-material/Gavel";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

function humanPenalty(penalty) {
  const map = {
    DriveThrough: { label: "Drive-through", color: "warning", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" /> },
    StopGo: { label: "Stop & Go", color: "error", icon: <GavelIcon fontSize="small" /> },
    Time: { label: "Time Penalty", color: "info", icon: <TimerOutlinedIcon fontSize="small" /> },
    Disqualified: { label: "Disqualified", color: "error", icon: <ErrorOutlineIcon fontSize="small" /> },
    PostRaceTime: { label: "Post-Race Time", color: "info", icon: <TimerOutlinedIcon fontSize="small" /> },
    Warning: { label: "Warning", color: "default", icon: <ErrorOutlineIcon fontSize="small" /> },
  };

  if (!map[penalty]) {
    const label = String(penalty || "Penalty")
      .replace(/([A-Z])/g, " $1")
      .trim();
    return { label, color: "default", icon: <ErrorOutlineIcon fontSize="small" /> };
  }

  return map[penalty];
}

function statusChip(clearedInLap) {
  const isCleared = clearedInLap !== null && clearedInLap !== undefined && clearedInLap > 0;
  return isCleared
    ? { icon: <CheckCircleOutlineIcon fontSize="small" />, label: `Cleared L${clearedInLap}`, color: "success", variant: "outlined" }
    : { icon: <HourglassEmptyIcon fontSize="small" />, label: "Pending", color: "warning", variant: "filled" };
}

function groupByCar(penalties) {
  const byCar = new Map();
  penalties.forEach((p) => {
    const key = p.carId ?? "—";
    if (!byCar.has(key)) byCar.set(key, []);
    byCar.get(key).push(p);
  });

  for (const arr of byCar.values()) {
    arr.sort((a, b) => (a.violationInLap ?? 0) - (b.violationInLap ?? 0));
  }

  return byCar;
}

function summarize(penalties) {
  const total = penalties.length;
  const pending = penalties.filter((p) => !(p.clearedInLap > 0)).length;

  const byType = penalties.reduce((acc, p) => {
    const k = p.penalty || "Other";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const typeChips = Object.entries(byType)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => {
      const hp = humanPenalty(k);
      return (
        <Chip
          key={k}
          size="small"
          label={`${hp.label}: ${v}`}
          color={hp.color}
          variant="outlined"
          sx={{ m: 0.5 }}
          icon={hp.icon}
        />
      );
    });

  return { total, pending, typeChips };
}

export default function PenaltiesPanel({ penalties = [], drivers = [], event }) {
  const [query, setQuery] = React.useState("");
  const [sortMode, setSortMode] = React.useState("lap");

  const carLookup = React.useMemo(() => {
    const map = new Map();

    const lines = event?.raw?.sessionResult?.leaderBoardLines || [];

    lines.forEach((line) => {
      const car = line?.car;
      const carId = car?.carId;
      if (carId == null) return;

      const carDrivers = Array.isArray(car?.drivers) ? car.drivers : [];
      const currentDriver = line?.currentDriver || null;
      const currentDriverIndex =
        Number.isInteger(line?.currentDriverIndex) ? line.currentDriverIndex : 0;

      map.set(carId, {
        raceNumber: car?.raceNumber,
        currentDriverIndex,
        currentDriverName: currentDriver
          ? [currentDriver.firstName, currentDriver.lastName].filter(Boolean).join(" ").trim()
          : null,
        drivers: carDrivers.map((d) => ({
          name: [d?.firstName, d?.lastName].filter(Boolean).join(" ").trim(),
          playerId: d?.playerId ?? null,
        })),
      });
    });

    return map;
  }, [event]);

  function driverNameFor(p) {
    const carInfo = carLookup.get(p.carId);

    if (carInfo) {
      const di = Number.isInteger(p?.driverIndex) ? p.driverIndex : null;

      if (di !== null && carInfo.drivers[di]?.name) {
        return carInfo.drivers[di].name;
      }

      if (carInfo.currentDriverName) {
        return carInfo.currentDriverName;
      }
    }

    const di = p?.driverIndex;
    if (Array.isArray(drivers) && Number.isInteger(di) && drivers[di]?.name) {
      return drivers[di].name;
    }

    return "Unknown Driver";
  }

  function carLabelFor(carId) {
    const carInfo = carLookup.get(carId);
    if (carInfo?.raceNumber != null) return `#${carInfo.raceNumber}`;
    if (carId != null) return `Car ${carId}`;
    return "Unknown Car";
  }

  const filtered = penalties.filter((p) => {
    if (!query) return true;

    const hay =
      `${driverNameFor(p)} ${p.reason || ""} ${p.penalty || ""} ${p.carId ?? ""} ${carLabelFor(p.carId)}`.toLowerCase();

    return hay.includes(query.trim().toLowerCase());
  });

  const severityRank = (code) => {
    switch (code) {
      case "Disqualified":
        return 5;
      case "StopGo":
        return 4;
      case "DriveThrough":
        return 3;
      case "PostRaceTime":
      case "Time":
        return 2;
      case "Warning":
        return 1;
      default:
        return 0;
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === "severity") {
      const s = severityRank(b.penalty) - severityRank(a.penalty);
      if (s !== 0) return s;
      return (a.violationInLap ?? 0) - (b.violationInLap ?? 0);
    }

    const lap = (a.violationInLap ?? 0) - (b.violationInLap ?? 0);
    if (lap !== 0) return lap;
    return severityRank(b.penalty) - severityRank(a.penalty);
  });

  const grouped = groupByCar(sorted);
  const { total, pending, typeChips } = summarize(sorted);

  return (
    <Box>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Chip size="small" label={`Total: ${total}`} color="default" sx={{ m: 0.5 }} />
          <Chip size="small" label={`Pending: ${pending}`} color="warning" sx={{ m: 0.5 }} />
          {typeChips}
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            size="small"
            placeholder="Filter by driver, reason, type, or car # …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 380 } }}
          />

          <ToggleButtonGroup
            exclusive
            value={sortMode}
            onChange={(_, v) => v && setSortMode(v)}
            size="small"
          >
            <ToggleButton value="lap">Sort by Lap</ToggleButton>
            <ToggleButton value="severity">Sort by Severity</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {Array.from(grouped.entries()).map(([carId, items]) => {
        const name = driverNameFor(items[0]);
        const carHeader = `${carLabelFor(carId)} • ${name}`;
        const pendingCount = items.filter((p) => !(p.clearedInLap > 0)).length;

        return (
          <Accordion key={carId} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 700 }}>{carHeader}</Typography>
                <Chip size="small" label={`${items.length} pen.`} variant="outlined" />
                {pendingCount > 0 && (
                  <Chip size="small" label={`${pendingCount} pending`} color="warning" />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small" sx={{ width: "100%", display: "block", overflowX: "auto" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">Severity</TableCell>
                      <TableCell>Lap</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((p, idx) => {
                      const hp = humanPenalty(p.penalty);
                      const st = statusChip(p.clearedInLap);

                      const valueHint =
                        p.penalty === "StopGo"
                          ? "Seconds (Stop&Go)"
                          : p.penalty === "Time" || p.penalty === "PostRaceTime"
                          ? "Seconds"
                          : p.penalty === "DriveThrough"
                          ? "Count"
                          : "Raw";

                      return (
                        <TableRow key={idx}>
                          <TableCell>
                            <Chip
                              size="small"
                              color={hp.color}
                              label={hp.label}
                              icon={hp.icon}
                              sx={{ minWidth: 140 }}
                            />
                          </TableCell>
                          <TableCell>
                            {p.reason && p.reason !== "None" ? (
                              <Typography variant="body2">{p.reason}</Typography>
                            ) : (
                              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={valueHint}>
                              <Chip size="small" label={p.penaltyValue ?? "—"} variant="outlined" />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {Number.isFinite(p.violationInLap) ? `L${p.violationInLap}` : "—"}
                            {p.clearedInLap > 0 && (
                              <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                                → L{p.clearedInLap}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={st.color}
                              variant={st.variant}
                              icon={st.icon}
                              label={st.label}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {sorted.length === 0 && (
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          No penalties recorded.
        </Typography>
      )}
    </Box>
  );
}